import { EEntityType, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CardBox from '@src/App/Components/Cards/CardBox/CardBox';
import Item from '@src/App/Components/Items/EntityItems/Item/Item';
import List, { generateTopXText } from '@src/App/Components/Lists/List/List';
import ProjectOverlay from '@src/App/Components/Overlays/ProjectOverlay/ProjectOverlay';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface ProjectsCardProps {
    title?: string;
    projects: {
        id: number;
        title: string;
        level?: number;
    }[];
    inOverlay?: boolean;
    limit?: number;
}

/**
 * Render a project card with given title, projects, and overlay indicator.
 *
 * @param {ProjectsCardProps} title - The title of the projects card.
 * @param {ProjectsCardProps} projects - The projects to be displayed in the card.
 * @param {ProjectsCardProps} inOverlay - The indicator if the card is in overlay mode.
 *
 * @returns {JSX.Element} The rendered project card.
 */
function ProjectsCard({ limit, title, projects, inOverlay }: ProjectsCardProps) {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).projects;
    const newTitle = generateTopXText(title ? title : langStrings.projects, limit);
    const limitedElements = limit ? projects.slice(0, limit > projects.length ? projects.length : limit) : projects;
    const content = limitedElements.map((project) => (
        <Item key={project.id} percent={0} onClick={entityTypeToLink(project.id, EEntityType.PROJECT)}>
            <ProjectOverlay key={project.id} disabled={inOverlay} projectId={project.id}>
                {project.title}
            </ProjectOverlay>
        </Item>
    ));
    return (
        <CardBox title={newTitle}>
            <List>{content}</List>
        </CardBox>
    );
}

export default ProjectsCard;
